// File: apps/web/src/components/collector-keys/TokenRevealModal.tsx
// Version: 2026.4.1 | Initial — Phase 2 Wave 2 collector-key UI
//
// One-time reveal of a freshly-minted token. The server only returns the
// plaintext in the mint / rotate response; after this modal closes, the
// user can never see the full token again. Warn visibly, require the
// user to click "I've saved it" to close, and never cache the plaintext
// anywhere downstream.

import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../engine/tokens';
import { SectionLabel, PulseDot } from '../engine/EnginePageShell';
import type { MintedCollectorKey } from '../../lib/api/types-collector-keys';

interface Props {
  minted:  MintedCollectorKey;
  onClose: () => void;
}

export function TokenRevealModal({ minted, onClose }: Props) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(minted.plainToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard API unavailable — user can still select & Ctrl+C from the code block.
    }
  };

  return (
    <div
      onClick={(e) => {
        // Disable click-outside-to-close so the user must consciously dismiss.
        e.stopPropagation();
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'engine-tooltip-in 0.25s ease',
      }}
    >
      <div style={{
        background: C.panel, border: `1px solid ${C.violet}40`, borderRadius: 16,
        padding: 28, width: '100%', maxWidth: 640,
        boxShadow: `0 0 0 1px ${C.violet}15, 0 20px 60px rgba(0,0,0,0.5)`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <PulseDot color={C.violet} />
          <SectionLabel color={C.violet}>Key Generated</SectionLabel>
        </div>
        <div style={{ fontSize: 12, fontFamily: F.mono, color: C.muted, marginBottom: 20 }}>
          Key <span style={{ color: C.text }}>{minted.name}</span> is now active.
        </div>

        {/* ── Warning banner ──────────────────────────────────────────────── */}
        <div style={{
          background: `${C.pink}12`, border: `1px solid ${C.pink}40`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 18,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <div style={{ fontSize: 18, lineHeight: 1 }}>⚠</div>
          <div>
            <div style={{ fontFamily: F.mono, fontSize: 11, color: C.pink, fontWeight: 600, letterSpacing: '0.08em', marginBottom: 4 }}>
              SAVE THIS TOKEN NOW
            </div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              This is the only time the full token will be displayed. Copy it to a password manager
              or paste it directly into your collector's <code style={{
                fontFamily: F.mono, fontSize: 11, background: 'rgba(0,0,0,0.3)',
                padding: '1px 6px', borderRadius: 4, color: C.text,
              }}>appsettings.json</code> now. If lost, rotate or revoke and generate a replacement.
            </div>
          </div>
        </div>

        {/* ── Token code block ────────────────────────────────────────────── */}
        <div style={{
          background: 'rgba(0,0,0,0.4)', border: `1px solid ${C.faint}`, borderRadius: 10,
          padding: 16, marginBottom: 14, position: 'relative',
        }}>
          <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 8 }}>
            BEARER TOKEN
          </div>
          <div
            style={{
              fontFamily: F.mono, fontSize: 13, color: C.text,
              wordBreak: 'break-all', lineHeight: 1.5,
              userSelect: 'all',
            }}
          >
            {minted.plainToken}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
          <button
            onClick={copy}
            style={{
              background: copied ? `${C.neon}20` : `${C.violet}15`,
              border: `1px solid ${copied ? `${C.neon}60` : `${C.violet}50`}`,
              color: copied ? C.neon : C.violet,
              fontFamily: F.mono, fontSize: 11, padding: '10px 22px', borderRadius: 8,
              cursor: 'pointer', fontWeight: 600, letterSpacing: '0.08em',
              transition: 'all 0.15s ease',
            }}
          >
            {copied ? '✓ COPIED' : '📋 COPY TO CLIPBOARD'}
          </button>
          <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
            or select the token above and Ctrl+C
          </div>
        </div>

        {/* ── Metadata summary ────────────────────────────────────────────── */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20,
          fontFamily: F.mono, fontSize: 11,
        }}>
          <div>
            <div style={{ color: C.dim, fontSize: 9, letterSpacing: '0.1em', marginBottom: 4 }}>PREFIX</div>
            <div style={{ color: C.text }}>{minted.prefix}</div>
          </div>
          <div>
            <div style={{ color: C.dim, fontSize: 9, letterSpacing: '0.1em', marginBottom: 4 }}>EXPIRES</div>
            <div style={{ color: C.text }}>
              {minted.expiresAt
                ? new Date(minted.expiresAt).toLocaleDateString()
                : 'Never (until revoked)'}
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ color: C.dim, fontSize: 9, letterSpacing: '0.1em', marginBottom: 4 }}>SCOPES</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {minted.scopes.map(s => (
                <span key={s} style={{
                  background: `${C.violet}15`, color: C.violet, fontFamily: F.mono, fontSize: 10,
                  padding: '3px 8px', borderRadius: 4, border: `1px solid ${C.violet}30`,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: copied ? `${C.neon}15` : `${C.amber}15`,
              border: `1px solid ${copied ? `${C.neon}50` : `${C.amber}50`}`,
              color: copied ? C.neon : C.amber,
              fontFamily: F.mono, fontSize: 11, padding: '10px 24px', borderRadius: 8,
              cursor: 'pointer', fontWeight: 600, letterSpacing: '0.08em',
            }}
          >
            {copied ? '✓ I\'VE SAVED IT — CLOSE' : 'CLOSE WITHOUT COPYING'}
          </button>
        </div>
      </div>
    </div>
  );
}
